import { addSelfHeat } from './effects.js';
import { beforeAlliedAttackRaw, beforeOwnAttackRaw, interceptAttack as rawInterceptAttack, interceptIncomingDamage as rawInterceptIncomingDamage, onAfterCombat as rawOnAfterCombat, onAnyEvent as rawOnAnyEvent, onAttackResolved as rawOnAttackResolved, onBeforeCombat as rawOnBeforeCombat, onDealDamage as rawOnDealDamage, onDeath as rawOnDeath, onEnterPlay as rawOnEnterPlay, onPlay as rawOnPlay, onRoundEnd as rawOnRoundEnd, onRoundStart as rawOnRoundStart, onTakeDamage as rawOnTakeDamage, } from './fragments.js';
import { oncePerInstance, oncePerRound } from './frequency.js';
function withMeta(ability, meta) {
    return Object.freeze({ ...ability, meta });
}
function isGameCardAbility(input) {
    return typeof input === 'object' && input !== null
        && input.source === 'game_card_ability';
}
/**
 * Semantic timing may wrap a stateful helper without recompiling it. This keeps
 * its stable abilityId/state/legacyKey byte-for-byte and only enriches the
 * authoring metadata used by checks and UI projection.
 */
function withSemanticMeta(ability, meta) {
    const frequency = ability.state?.kind === 'once_per_round'
        ? 'first_per_round'
        : ability.state?.kind === 'once_per_instance'
            ? 'first_per_instance'
            : undefined;
    return withMeta(ability, {
        ...meta,
        ...(frequency !== undefined ? { frequency } : {}),
        ...(ability.abilityId !== undefined ? { abilityId: ability.abilityId } : {}),
    });
}
/** Появление (onEnterPlay): вскрытие плана, токен или возврат. */
export function onAppear(input, opts = {}) {
    const base = rawOnEnterPlay(input);
    const cause = opts.cause;
    const compile = cause === undefined
        ? base.compile
        : (cardId) => {
            const inner = base.compile(cardId);
            return ((ctx, ev) => (ev.cause === cause ? inner(ctx, ev) : undefined));
        };
    const meta = { timing: 'appear', event: 'UnitEnteredPlay', ...(cause !== undefined ? { cause } : {}) };
    return Object.freeze({ ...base, compile, meta });
}
/** Розыгрыш одноразовой SPELL/VIRUS (onPlay). */
export const onResolve = (input) => withMeta(rawOnPlay(input), { timing: 'resolve' });
/** Гибель (onDeath, любая причина); опциональный узкий фильтр по причине. */
export function onDeath(input, opts = {}) {
    const base = rawOnDeath(input);
    const cause = opts.cause;
    const compile = cause === undefined
        ? base.compile
        : (cardId) => {
            const inner = base.compile(cardId);
            return ((ctx, ev) => (ev.cause === cause ? inner(ctx, ev) : undefined));
        };
    const meta = {
        timing: 'death',
        event: 'UnitDied',
        ...(cause !== undefined ? { cause } : {}),
    };
    return Object.freeze({ ...base, compile, meta });
}
/** Постоянно: чистый производный провайдер StatMod-аур (реконсилятор движка). */
export function ongoing(provider, opts = {}) {
    return Object.freeze({
        source: 'game_card_ability',
        hook: 'ongoing',
        dependencies: Object.freeze([...(opts.dependencies ?? [])]),
        meta: { timing: 'ongoing' },
        compile: () => provider,
    });
}
export const beforeCombat = (input) => withMeta(rawOnBeforeCombat(input), { timing: 'before_combat' });
export const afterCombat = (input) => withSemanticMeta(isGameCardAbility(input) ? input : rawOnAfterCombat(input), { timing: 'after_combat' });
export const atRoundStart = (input) => withMeta(rawOnRoundStart(input), { timing: 'round_start' });
export const atRoundEnd = (input) => withMeta(rawOnRoundEnd(input), { timing: 'round_end' });
/** После атаки — унифицировано на завершённую attack group (AttackResolved). */
export const afterAttack = (input) => withSemanticMeta(isGameCardAbility(input) ? input : rawOnAttackResolved(input), { timing: 'after_attack', event: 'AttackResolved' });
/** Греется — после каждой своей завершённой атаки получает Перегрев 1. */
export const heatsUp = () => afterAttack(addSelfHeat(1));
export const afterDamageDealt = (input) => withMeta(rawOnDealDamage(input), { timing: 'after_damage_dealt', event: 'DamageDealt' });
export const afterDamageTaken = (input) => withMeta(rawOnTakeDamage(input), { timing: 'after_damage_taken', event: 'DamageDealt' });
/** Broad event timing for one ability that intentionally handles several event types. */
export const afterAnyEvent = (input) => withSemanticMeta(isGameCardAbility(input) ? input : rawOnAnyEvent(input), { timing: 'after_event' });
export const beforeAttack = (input) => withSemanticMeta(isGameCardAbility(input) ? input : rawInterceptAttack(input), { timing: 'before_attack' });
export const beforeOwnAttack = (input) => withSemanticMeta(isGameCardAbility(input) ? input : beforeOwnAttackRaw(input), { timing: 'before_own_attack' });
export const beforeAlliedAttack = (input) => withSemanticMeta(isGameCardAbility(input) ? input : beforeAlliedAttackRaw(input), { timing: 'before_allied_attack' });
export const beforeDamage = (input) => withSemanticMeta(isGameCardAbility(input) ? input : rawInterceptIncomingDamage(input), { timing: 'before_damage' });
export function afterEvent(input) {
    const meta = {
        timing: 'after_event', event: input.event,
        ...(input.frequency !== undefined ? { frequency: input.frequency } : {}),
        ...(input.abilityId !== undefined ? { abilityId: input.abilityId } : {}),
    };
    const isMatch = (event) => event.type === input.event;
    const runEffect = typeof input.run === 'function' ? undefined : input.run;
    const runFn = typeof input.run === 'function' ? input.run : undefined;
    const callRun = (ctx, event) => runFn !== undefined ? runFn(ctx, event) : runEffect.run(ctx, event);
    if (input.frequency === 'first_per_round' || input.frequency === 'first_per_instance') {
        if (input.abilityId === undefined) {
            throw new Error('afterEvent: frequency требует abilityId');
        }
        // CardEffect передаётся как есть (oncePerRound извлечёт dependencies).
        const run = runEffect ?? ((ctx, ...args) => runFn(ctx, args[0]));
        const frequency = input.frequency === 'first_per_round' ? oncePerRound : oncePerInstance;
        const ability = frequency({
            abilityId: input.abilityId,
            trigger: 'onAnyEvent',
            ...(input.legacyKey !== undefined ? { legacyKey: input.legacyKey } : {}),
            when: (ctx, ...args) => {
                const event = args[0];
                return isMatch(event) && (input.when?.(ctx, event) ?? true);
            },
            run,
        });
        return withMeta(ability, meta);
    }
    const filteredRun = (ctx, event) => {
        if (!isMatch(event))
            return undefined;
        if (input.when !== undefined && !input.when(ctx, event))
            return undefined;
        return callRun(ctx, event);
    };
    // CardEffect-форма сохраняет dependencies через ReactiveAbilityInput.
    const anyInput = runEffect !== undefined
        ? { source: 'card_effect', dependencies: runEffect.dependencies, run: (ctx, ...args) => filteredRun(ctx, args[0]) }
        : filteredRun;
    return withMeta(rawOnAnyEvent(anyInput), meta);
}
//# sourceMappingURL=semantic.js.map